import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import Header from "../components/app/Header";
import CreateListingDialog from "./CreateListingDialog";
import CommissionDemandDialog from "./CommissionDemandDialog";
import UpcomingWidget from "../components/app/UpcomingWidget";
import EmailEngagementCard from "../components/app/EmailEngagementCard";
import ListingTabs, { isTransactionHubUnlocked } from "../components/app/ListingTabs";
import IntakeDetails from "./IntakeDetails";
import CollaboratorsCard from "../components/app/CollaboratorsCard";
import CollaboratorCommentsFeed from "../components/app/CollaboratorCommentsFeed";
import SendTCPackageButton from "../components/app/SendTCPackageButton";
import { PHASES, STATUS_STYLES, computeProgress, computePhaseProgress } from "../lib/phases";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { ChevronLeft, Pencil, MapPin, User, Calendar, Hash, AlertTriangle, Wand2, Share2, Copy, Check, Mail, MessageSquare, Eye, FileText, Send, Trash2, CalendarPlus, X, ShieldCheck, Lock, CheckCircle2, RotateCcw, Ban } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

function useDebouncedCallback(fn, delay = 500) {
  const [timers] = useState({ ref: null });
  return (...args) => {
    if (timers.ref) clearTimeout(timers.ref);
    timers.ref = setTimeout(() => fn(...args), delay);
  };
}

function formatRelativeTime(iso) {
  try {
    const then = new Date(iso).getTime();
    const diffSec = Math.max(0, (Date.now() - then) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function ClosingCountdownPill({ closingDate }) {
  if (!closingDate) return null;
  const target = new Date(`${closingDate}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((target - today) / 86400000);
  const dateLabel = target.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  let statusLabel;
  let tone;
  if (days > 7) {
    statusLabel = `Closes in ${days} days`;
    tone = "bg-emerald-50 border-emerald-200 text-emerald-800";
  } else if (days > 0) {
    statusLabel = days === 1 ? "Closes tomorrow" : `Closes in ${days} days`;
    tone = "bg-amber-50 border-amber-300 text-amber-900";
  } else if (days === 0) {
    statusLabel = "Closing today";
    tone = "bg-amber-100 border-amber-400 text-amber-900";
  } else {
    statusLabel = `Closed ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
    tone = "bg-stone-100 border-stone-300 text-stone-700";
  }
  return (
    <div
      data-testid="phase-7-closing-pill"
      className={`inline-flex items-center gap-2 mt-2 rounded-full border px-3 h-7 text-[11px] font-semibold uppercase tracking-widest ${tone}`}
    >
      <Calendar className="h-3 w-3" />
      <span>{statusLabel}</span>
      <span className="opacity-60">·</span>
      <span className="normal-case font-normal tracking-normal">{dateLabel}</span>
    </div>
  );
}

function SellerSigningInline({ listing, onListingUpdate }) {
  const [value, setValue] = useState(listing?.seller_signing_date || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(listing?.seller_signing_date || "");
  }, [listing?.seller_signing_date]);

  const save = async (newValue) => {
    if ((newValue || "") === (listing?.seller_signing_date || "")) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/listings/${listing.id}`, {
        ...listing,
        seller_signing_date: newValue || null,
      });
      onListingUpdate(data);
      toast.success(newValue ? "Seller signing date saved" : "Seller signing date cleared");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save date");
      setValue(listing?.seller_signing_date || "");
    } finally {
      setSaving(false);
    }
  };

  const clear = () => { setValue(""); save(""); };

  const prettyDate = value
    ? new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
        weekday: "short", month: "short", day: "numeric", year: "numeric",
      })
    : null;

  return (
    <div
      data-testid="seller-signing-inline"
      className="my-3 rounded-md border border-indigo-200 bg-indigo-50/40 p-4 flex flex-wrap items-center gap-3"
    >
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-indigo-700" />
        <span className="text-[11px] uppercase tracking-widest text-indigo-800 font-semibold">
          Seller escrow signing
        </span>
      </div>
      <div className="flex items-center gap-2 flex-1 min-w-[220px]">
        <Input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={(e) => save(e.target.value)}
          data-testid="seller-signing-date-input"
          className="h-9 max-w-[190px] border-indigo-300 bg-white text-sm"
          disabled={saving}
        />
        {prettyDate && (
          <span className="text-xs text-indigo-800 font-medium hidden sm:inline">
            {prettyDate}
          </span>
        )}
      </div>
      {value && (
        <button
          type="button"
          onClick={clear}
          data-testid="seller-signing-clear"
          className="text-[11px] text-indigo-700 hover:text-indigo-900 underline"
          disabled={saving}
        >
          Clear
        </button>
      )}
      <p className="w-full text-[11px] text-indigo-800/80 leading-relaxed">
        The day your sellers sit down with escrow to sign closing docs. Shown on the seller share portal.
      </p>
    </div>
  );
}

function SoldMlsUploadCard({ listing, onListingUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const hasSold = !!listing?.sold_mls_uploaded_at;

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (!/\.(pdf|png|jpe?g)$/i.test(file.name)) {
      toast.error("Please upload a PDF or image (PNG/JPG) of the SOLD MLS sheet.");
      return;
    }
    setUploading(true);
    const t = toast.loading("Uploading SOLD MLS sheet…");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/listings/${listing.id}/sold-mls`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      onListingUpdate(data);
      toast.success("SOLD MLS sheet attached to this listing", { id: t });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not upload", { id: t });
    } finally {
      setUploading(false);
    }
  };

  const removeSold = async () => {
    try {
      const { data } = await api.delete(`/listings/${listing.id}/sold-mls`);
      onListingUpdate(data);
      toast.success("SOLD MLS sheet removed");
      setConfirmingDelete(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
  };

  const uploadedDate = listing?.sold_mls_uploaded_at
    ? new Date(listing.sold_mls_uploaded_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <div
      data-testid="sold-mls-upload-card"
      className="my-4 ml-10 rounded-md border border-emerald-200 bg-emerald-50/40 p-5"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="shrink-0 mt-0.5">
          <div className="h-9 w-9 rounded-full bg-emerald-700 text-white flex items-center justify-center">
            <FileText className="h-4 w-4" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline flex-wrap gap-2">
            <p className="text-sm font-semibold text-stone-900">SOLD MLS printout sheet</p>
            {hasSold && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-300 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">
                <CheckCircle2 className="h-3 w-3" />
                On file
              </span>
            )}
          </div>
          <p className="text-[12px] text-stone-600 mt-0.5 leading-relaxed">
            After changing MLS status to Sold, attach the printout for your records and audit trail. PDF or image, 25MB max.
          </p>

          {hasSold ? (
            <div className="mt-4 flex items-center flex-wrap gap-2">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/listings/${listing.id}/sold-mls/download`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sold-mls-open"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-white text-xs font-semibold bg-emerald-700 hover:bg-emerald-800"
              >
                <Eye className="h-3.5 w-3.5" />
                Open SOLD sheet
              </a>
              <label
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-stone-300 bg-white text-stone-700 text-xs font-semibold cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : "hover:bg-stone-50"}`}
                data-testid="sold-mls-replace"
              >
                <Send className="h-3.5 w-3.5" />
                {uploading ? "Uploading…" : "Replace"}
                <input type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={onFileSelected} disabled={uploading} />
              </label>
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                data-testid="sold-mls-remove"
                disabled={uploading}
                className="text-[11px] text-rose-600 hover:text-rose-800 underline"
              >
                Remove
              </button>
              <span className="text-[11px] text-stone-500 ml-1">
                <span className="font-medium text-stone-700">{listing.sold_mls_filename}</span>
                {uploadedDate && <> · {uploadedDate}</>}
              </span>
            </div>
          ) : (
            <div className="mt-3">
              <label
                className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-white text-xs font-semibold cursor-pointer bg-emerald-700 hover:bg-emerald-800 ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                data-testid="sold-mls-upload-label"
              >
                <FileText className="h-4 w-4" />
                {uploading ? "Uploading…" : "Upload SOLD MLS sheet"}
                <input type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={onFileSelected} disabled={uploading} />
              </label>
              <p className="mt-2 text-[11px] text-stone-500">PDF, PNG, or JPG · 25MB max</p>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent data-testid="sold-mls-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Remove SOLD MLS sheet?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears the attached file. You can re-upload any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Nevermind</AlertDialogCancel>
            <AlertDialogAction onClick={removeSold} className="bg-rose-600 hover:bg-rose-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function SellerSigningChecklistLink() {
  return (
    <div className="my-3">
      <a
        href="https://www.ortconline.com/Web2/downloads/english/Sellers-Checklist.pdf"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="seller-signing-checklist-link"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white"
        style={{ backgroundColor: "#010082" }}
      >
        Seller Signing Checklist
        <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M3 9L9 3M9 3H4.5M9 3v4.5"/></svg>
      </a>
      <p className="mt-1.5 text-[11px] text-stone-500 leading-relaxed">
        Old Republic Title PDF — share with sellers before signing.
      </p>
    </div>
  );
}

function RPAUploadCard({ listing, onListingUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const hasRPA = !!listing?.rpa_uploaded_at;
  const rpaShareUrl = listing?.rpa_share_token
    ? `${window.location.origin}/api/public/rpa/${listing.rpa_share_token}`
    : "";

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload the RPA as a PDF.");
      return;
    }
    setUploading(true);
    const t = toast.loading("Uploading & extracting dates from the RPA…");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/listings/${listing.id}/rpa`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      onListingUpdate(data);
      const populated = Object.values(data.rpa_extracted_dates || {}).filter(Boolean).length;
      toast.success(`RPA uploaded · ${populated} date${populated === 1 ? "" : "s"} applied to Phase 07`, { id: t });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not process the RPA", { id: t });
    } finally {
      setUploading(false);
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(rpaShareUrl);
      toast.success("RPA link copied");
    } catch (_) {
      toast.error("Could not copy");
    }
  };

  const removeRPA = async () => {
    try {
      const { data } = await api.delete(`/listings/${listing.id}/rpa`);
      onListingUpdate(data);
      toast.success("RPA removed from this listing");
      setConfirmingDelete(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
  };

  const uploadedDate = listing?.rpa_uploaded_at
    ? new Date(listing.rpa_uploaded_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <div
      data-testid="rpa-upload-card"
      className="mb-6 rounded-md border border-[#010082]/25 bg-[#010082]/[0.035] p-5"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="shrink-0 mt-0.5">
          <div className="h-9 w-9 rounded-full bg-[#010082] text-white flex items-center justify-center">
            <FileText className="h-4 w-4" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline flex-wrap gap-2">
            <p className="text-sm font-semibold text-stone-900">Ratified Purchase Agreement</p>
            {hasRPA && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-200 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">
                <CheckCircle2 className="h-3 w-3" />
                Uploaded
              </span>
            )}
          </div>
          <p className="text-[12px] text-stone-600 mt-0.5 leading-relaxed">
            Upload the fully ratified Residential Purchase Agreement. All Phase 07 contingency dates + close of escrow are extracted automatically and any date landing on a weekend rolls to the next business day.
          </p>

          {hasRPA ? (
            <div className="mt-4 space-y-3">
              <div className="flex items-center flex-wrap gap-2">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/listings/${listing.id}/rpa/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="rpa-open-link"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-white text-xs font-semibold"
                  style={{ backgroundColor: "#010082" }}
                >
                  <Eye className="h-3.5 w-3.5" />
                  Open Agreement
                </a>
                {rpaShareUrl && (
                  <Button
                    variant="outline"
                    onClick={copyShare}
                    data-testid="rpa-copy-share"
                    className="h-9 border-stone-300 rounded-full px-3 text-xs"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1.5" />
                    Copy share link
                  </Button>
                )}
                <label
                  className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-stone-300 bg-white text-stone-700 text-xs font-semibold cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : "hover:bg-stone-50"}`}
                  data-testid="rpa-reupload"
                >
                  <Send className="h-3.5 w-3.5" />
                  {uploading ? "Uploading…" : "Replace"}
                  <input type="file" accept="application/pdf" className="hidden" onChange={onFileSelected} disabled={uploading} />
                </label>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(true)}
                  data-testid="rpa-remove"
                  disabled={uploading}
                  className="text-[11px] text-rose-600 hover:text-rose-800 underline"
                >
                  Remove
                </button>
              </div>
              <div className="text-[11px] text-stone-500">
                <span className="font-medium text-stone-700">{listing.rpa_filename}</span>
                {uploadedDate && <> · uploaded {uploadedDate}</>}
              </div>
              {listing.rpa_extracted_dates && Object.values(listing.rpa_extracted_dates).some(Boolean) && (
                <details className="text-[11px] text-stone-600" data-testid="rpa-dates-details">
                  <summary className="cursor-pointer font-semibold text-stone-700 uppercase tracking-widest text-[10px]">
                    Extracted dates
                  </summary>
                  <ul className="mt-2 space-y-1 pl-1">
                    {Object.entries(listing.rpa_extracted_dates)
                      .filter(([, v]) => !!v)
                      .map(([k, v]) => (
                        <li key={k} className="flex justify-between gap-3 tabular-nums">
                          <span className="capitalize text-stone-600">
                            {k.replace(/_/g, " ").replace(/date$/, "").trim()}
                          </span>
                          <span className="font-mono text-stone-800">{v}</span>
                        </li>
                      ))}
                  </ul>
                </details>
              )}
            </div>
          ) : (
            <div className="mt-3">
              <label
                className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-white text-xs font-semibold cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                style={{ backgroundColor: "#010082" }}
                data-testid="rpa-upload-label"
              >
                <FileText className="h-4 w-4" />
                {uploading ? "Uploading & extracting…" : "Upload Ratified Purchase Agreement (PDF)"}
                <input type="file" accept="application/pdf" className="hidden" onChange={onFileSelected} disabled={uploading} />
              </label>
              <p className="mt-2 text-[11px] text-stone-500">Max 25 MB · PDF only</p>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent data-testid="rpa-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Remove Ratified Purchase Agreement?</AlertDialogTitle>
            <AlertDialogDescription>
              This unpublishes the agreement link from the seller share portal and clears the file reference. Extracted dates already applied to Phase 07 stay put — you can re-upload anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Nevermind</AlertDialogCancel>
            <AlertDialogAction onClick={removeRPA} className="bg-rose-600 hover:bg-rose-700">
              Remove Agreement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CommissionDemandInline({ listing, onListingUpdate }) {
  const [open, setOpen] = useState(false);
  const [ackBusy, setAckBusy] = useState(false);
  const sent = !!(listing?.tasks?.t6_commission_demand?.completed_at);
  const ackAt = listing?.commission_demand_ack_at;
  const ackLabel = ackAt
    ? new Date(ackAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  const logAck = async () => {
    if (ackBusy) return;
    setAckBusy(true);
    try {
      await api.post(`/listings/${listing.id}/commission-demand/ack`);
      const { data: fresh } = await api.get(`/listings/${listing.id}`);
      onListingUpdate?.(fresh);
      toast.success("Escrow acknowledgment logged");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not log");
    } finally {
      setAckBusy(false);
    }
  };

  return (
    <div className="my-3 flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="commission-demand-open"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white"
        style={{ backgroundColor: "#010082" }}
      >
        {sent ? "Re-send Commission Demand" : "Generate Commission Demand"}
        <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M3 9L9 3M9 3H4.5M9 3v4.5"/></svg>
      </button>
      {sent && !ackAt && (
        <button
          type="button"
          onClick={logAck}
          disabled={ackBusy}
          data-testid="commission-demand-ack"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
        >
          {ackBusy ? "Logging…" : "Log escrow ack"}
        </button>
      )}
      {ackAt && (
        <span
          data-testid="commission-demand-ack-chip"
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-200"
          title={new Date(ackAt).toLocaleString()}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
          Ack {ackLabel}
        </span>
      )}
      <CommissionDemandDialog
        open={open}
        onOpenChange={setOpen}
        listing={listing}
        onListingUpdate={onListingUpdate}
      />
    </div>
  );
}


function CloseOfEscrowInline({ listing, onListingUpdate }) {
  const [value, setValue] = useState(listing?.closing_date || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(listing?.closing_date || "");
  }, [listing?.closing_date]);

  const save = async (newValue) => {
    if ((newValue || "") === (listing?.closing_date || "")) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/listings/${listing.id}`, {
        ...listing,
        closing_date: newValue || null,
      });
      onListingUpdate(data);
      toast.success(newValue ? "Close of escrow date saved" : "Close of escrow date cleared");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save date");
      setValue(listing?.closing_date || "");
    } finally {
      setSaving(false);
    }
  };

  const clear = () => {
    setValue("");
    save("");
  };

  const prettyDate = value
    ? new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div
      data-testid="close-of-escrow-inline"
      className="my-3 rounded-md border border-emerald-200 bg-emerald-50/40 p-4 flex flex-wrap items-center gap-3"
    >
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-emerald-700" />
        <span className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">
          Close of escrow
        </span>
      </div>
      <div className="flex items-center gap-2 flex-1 min-w-[220px]">
        <Input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={(e) => save(e.target.value)}
          data-testid="close-of-escrow-date-input"
          className="h-9 max-w-[190px] border-emerald-300 bg-white text-sm"
          disabled={saving}
        />
        {prettyDate && (
          <span className="text-xs text-emerald-800 font-medium hidden sm:inline">
            {prettyDate}
          </span>
        )}
      </div>
      {value && (
        <button
          type="button"
          onClick={clear}
          data-testid="close-of-escrow-clear"
          className="text-[11px] text-emerald-700 hover:text-emerald-900 underline"
          disabled={saving}
        >
          Clear
        </button>
      )}
      <p className="w-full text-[11px] text-emerald-800/80 leading-relaxed">
        {value
          ? "Shown on the seller share portal, dashboard countdown, and every seller email."
          : "Set the recorded/close date so it appears on the seller share portal, dashboard countdown, and every seller email."}
      </p>
    </div>
  );
}



function ActivityLog({ listing, onListingUpdate }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [secOpen, setSecOpen] = useState(false);
  const [secText, setSecText] = useState(
    "Home verified secure — all doors and windows locked, alarm re-armed, property left as found."
  );
  const [secNotes, setSecNotes] = useState("");
  const [secBusy, setSecBusy] = useState(false);
  const notes = (listing.activity_notes || []).slice().sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/activity-notes`, { text: trimmed });
      onListingUpdate(data);
      setText("");
      toast.success("Note saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const openSecurityCheck = useCallback(() => {
    setSecText(
      "Home verified secure — all doors and windows locked, alarm re-armed, property left as found."
    );
    setSecNotes("");
    setSecOpen(true);
  }, []);

  useEffect(() => {
    const handler = () => openSecurityCheck();
    window.addEventListener("eh:open-security-check", handler);
    return () => window.removeEventListener("eh:open-security-check", handler);
  }, [openSecurityCheck]);

  const submitSecurityCheck = async () => {
    const base = secText.trim() || "Home verified secure.";
    const extra = secNotes.trim();
    const composed = extra ? `${base}\n\nAdditional notes: ${extra}` : base;
    setSecBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/activity-notes`, {
        text: composed,
        type: "security_check",
      });
      onListingUpdate(data);
      setSecOpen(false);
      toast.success("Security check logged");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not log check");
    } finally {
      setSecBusy(false);
    }
  };

  const remove = async (noteId) => {
    try {
      const { data } = await api.delete(`/listings/${listing.id}/activity-notes/${noteId}`);
      onListingUpdate(data);
      toast.success("Note removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setToDelete(null);
    }
  };

  const nowStamp = new Date().toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <section data-testid="activity-log" className="mt-14">
      <div className="hr-serif mb-10" />
      <div className="flex items-start gap-6">
        <span className="section-index text-stone-500 text-sm pt-1 hidden sm:block">Log</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
            <div className="flex items-baseline gap-2">
              <FileText className="h-4 w-4 text-stone-500" />
              <h2 className="text-xl font-heading font-semibold tracking-tight text-stone-900">
                Activity Log &amp; Misc Notes
              </h2>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={openSecurityCheck}
              data-testid="log-security-check-btn"
              className="rounded-full border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-900 h-8 px-3.5 text-xs font-medium"
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
              Log Security Check
            </Button>
          </div>
          <p className="text-sm text-stone-500 mb-6 leading-relaxed max-w-2xl">
            Log context, observations, or off-checklist updates. Notes logged today are automatically included in tonight&rsquo;s seller digest email.
          </p>

          <div className="rounded-md border border-stone-200 bg-white p-5 mb-6">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a note about the listing, seller conversation, showing feedback, or anything worth remembering…"
              rows={3}
              data-testid="activity-note-input"
              className="border-stone-300 bg-white text-sm"
            />
            <div className="mt-3 flex items-center justify-between">
              <p className="text-[11px] text-stone-500">
                {text.length > 0 && `${text.length} char${text.length === 1 ? "" : "s"}`}
              </p>
              <Button
                onClick={submit}
                disabled={!text.trim() || busy}
                data-testid="activity-note-save"
                className="rounded-full px-4 text-white"
                style={{ backgroundColor: "#010082" }}
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {busy ? "Saving..." : "Save note"}
              </Button>
            </div>
          </div>

          {notes.length === 0 ? (
            <div className="text-sm text-stone-500 italic pl-4 border-l-2 border-stone-200">
              No notes logged yet.
            </div>
          ) : (
            <ol className="space-y-4">
              {notes.map((n) => {
                const isSecurity = (n.type || "note") === "security_check";
                return (
                  <li
                    key={n.id}
                    data-testid={`activity-note-${n.id}`}
                    className={`rounded-md border p-5 relative group ${
                      isSecurity
                        ? "border-emerald-200 bg-emerald-50/40"
                        : "border-stone-200 bg-white"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        {isSecurity && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5"
                            data-testid={`security-badge-${n.id}`}
                          >
                            <ShieldCheck className="h-3 w-3" />
                            Security Check
                          </span>
                        )}
                        <span className="text-sm font-semibold text-stone-900">{n.author_name || "Agent"}</span>
                        <span className="text-[10px] uppercase tracking-widest text-stone-400">
                          {new Date(n.created_at).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}{" "}
                          · {formatRelativeTime(n.created_at)}
                        </span>
                      </div>
                      <button
                        onClick={() => setToDelete(n)}
                        data-testid={`activity-note-delete-${n.id}`}
                        className="text-stone-400 hover:text-rose-600 transition-colors opacity-0 group-hover:opacity-100"
                        aria-label="Delete note"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{n.text}</p>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Remove note?</AlertDialogTitle>
            <AlertDialogDescription>
              This activity log entry will be permanently removed. Sellers who already received today's digest email will still see the note there.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(toDelete.id)} className="bg-rose-600 hover:bg-rose-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={secOpen} onOpenChange={setSecOpen}>
        <DialogContent data-testid="security-check-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-emerald-100 text-emerald-700">
                <ShieldCheck className="h-4 w-4" />
              </span>
              <DialogTitle className="font-heading tracking-tight text-xl">Log Security Check</DialogTitle>
            </div>
            <DialogDescription>
              Reassure your seller that the home is locked up and secure after a showing or visit. This will be timestamped, added to the activity log, and included in tonight&rsquo;s seller digest email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2 flex items-center gap-2">
              <Lock className="h-3.5 w-3.5 text-stone-500" />
              <span className="text-[11px] uppercase tracking-widest text-stone-500">Timestamp</span>
              <span className="text-sm text-stone-800 font-medium ml-auto" data-testid="security-check-timestamp">
                {nowStamp}
              </span>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 mb-1.5 block">
                Reassurance message
              </Label>
              <Textarea
                value={secText}
                onChange={(e) => setSecText(e.target.value)}
                rows={3}
                data-testid="security-check-message"
                className="border-stone-300 bg-white text-sm"
              />
              <p className="text-[11px] text-stone-500 mt-1">
                Pre-filled with a standard reassurance. Edit if needed.
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 mb-1.5 block">
                Additional notes <span className="text-stone-400 normal-case tracking-normal">(optional)</span>
              </Label>
              <Textarea
                value={secNotes}
                onChange={(e) => setSecNotes(e.target.value)}
                rows={2}
                placeholder="e.g., Left interior lights on, front gate latched, packages moved inside…"
                data-testid="security-check-notes"
                className="border-stone-300 bg-white text-sm"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setSecOpen(false)}
              disabled={secBusy}
              data-testid="security-check-cancel"
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={submitSecurityCheck}
              disabled={secBusy}
              data-testid="security-check-submit"
              className="rounded-full text-white bg-emerald-700 hover:bg-emerald-800"
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
              {secBusy ? "Logging..." : "Log Security Check"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}


function ShareCard({ listing, onUpdate }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = listing.share_token
    ? `${window.location.origin}/share/${listing.share_token}`
    : "";

  const toggle = async (enabled) => {
    try {
      const { data } = await api.post(`/listings/${listing.id}/share`, { enabled });
      onUpdate(data);
      toast.success(enabled ? "Seller share link enabled" : "Seller share link disabled");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Could not copy");
    }
  };

  return (
    <div data-testid="share-card" className="rounded-md border border-stone-200 bg-white p-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Share2 className="h-4 w-4 text-stone-600" />
          <h3 className="text-sm font-heading font-semibold tracking-tight text-stone-900">
            Share with seller
          </h3>
        </div>
        <Switch
          data-testid="share-toggle"
          checked={!!listing.share_enabled}
          onCheckedChange={toggle}
        />
      </div>
      {listing.share_enabled && listing.share_token ? (
        <div className="space-y-2">
          <p className="text-xs text-stone-500 leading-relaxed">
            A read-only, Equitas-branded view. No notes or initials shown to the seller.
          </p>
          <div className="flex gap-2 items-center rounded-md border border-stone-200 bg-stone-50 px-2.5 py-1.5">
            <span data-testid="share-url" className="text-[11px] text-stone-700 font-mono truncate flex-1">
              {shareUrl}
            </span>
            <button
              onClick={copy}
              data-testid="share-copy"
              className="shrink-0 text-stone-500 hover:text-stone-900 transition-colors"
              aria-label="Copy link"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>

          <div
            data-testid="share-engagement"
            className="flex items-center gap-2 pt-1 text-[11px] text-stone-500"
          >
            <Eye className="h-3 w-3 shrink-0" />
            {listing.share_view_count > 0 ? (
              <span>
                Opened{" "}
                <span className="font-semibold text-stone-700 tabular-nums">
                  {listing.share_view_count}
                </span>{" "}
                time{listing.share_view_count === 1 ? "" : "s"}
                {listing.share_last_viewed_at && (
                  <>
                    {" · last "}
                    {formatRelativeTime(listing.share_last_viewed_at)}
                  </>
                )}
              </span>
            ) : (
              <span className="italic">Not opened yet — nudge your seller.</span>
            )}
          </div>
        </div>
      ) : (
        <p className="text-xs text-stone-500 leading-relaxed">
          Toggle on to generate a private share link your seller can watch progress on.
        </p>
      )}
    </div>
  );
}

const OH_STATUS_STYLES = {
  scheduled: "bg-[#010082]/[0.08] text-[#010082] border-[#010082]/25",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-stone-100 text-stone-500 border-stone-300 line-through",
};

function OpenHouseEvents({ listing, onListingUpdate }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ date: "", status: "scheduled", notes: "" });
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const events = (listing.open_house_events || []).slice().sort(
    (a, b) => (a.date || "").localeCompare(b.date || "")
  );

  // Detect if a security_check was already logged today (Pacific or user-local — close enough for a UX nudge)
  const hasSecurityCheckToday = useMemo(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const d = today.getDate();
    return (listing.activity_notes || []).some((n) => {
      if ((n.type || "note") !== "security_check") return false;
      const ca = new Date(n.created_at);
      return ca.getFullYear() === y && ca.getMonth() === m && ca.getDate() === d;
    });
  }, [listing.activity_notes]);

  const openSecurityCheckModal = () => {
    window.dispatchEvent(new CustomEvent("eh:open-security-check"));
    // Ensure the modal (mounted deep in ActivityLog) is scrolled into view for context
    const el = document.querySelector('[data-testid="activity-log"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const nudgeSecurityCheck = (message) => {
    toast(message, {
      duration: 8000,
      icon: <ShieldCheck className="h-4 w-4 text-emerald-600" />,
      action: {
        label: "Log security check",
        onClick: openSecurityCheckModal,
      },
    });
  };

  const create = async () => {
    if (!draft.date) {
      toast.error("Pick a date");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/open-houses`, draft);
      onListingUpdate(data);
      const wasCompleted = draft.status === "completed";
      setDraft({ date: "", status: "scheduled", notes: "" });
      setAdding(false);
      toast.success("Open house added");
      if (wasCompleted && !hasSecurityCheckToday) {
        setTimeout(
          () => nudgeSecurityCheck("Open house wrapped up — log a security check to reassure your seller."),
          400,
        );
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, changes) => {
    const prev = events.find((ev) => ev.id === id);
    try {
      const { data } = await api.patch(`/listings/${listing.id}/open-houses/${id}`, changes);
      onListingUpdate(data);
      if (
        changes.status === "completed" &&
        prev &&
        prev.status !== "completed" &&
        !hasSecurityCheckToday
      ) {
        nudgeSecurityCheck("Open house marked complete — log a security check to reassure your seller.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/listings/${listing.id}/open-houses/${id}`);
      onListingUpdate(data);
      toast.success("Open house removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div data-testid="open-house-events" className="mt-4 pl-1">
      {events.length > 0 && (
        <ul className="space-y-2 mb-3">
          {events.map((e) => {
            const isToday = e.completed_at
              ? new Date(e.completed_at).toDateString() === new Date().toDateString()
              : (e.date && e.date === new Date().toISOString().slice(0, 10));
            const needsSecurityNudge = e.status === "completed" && isToday && !hasSecurityCheckToday;
            return (
            <li
              key={e.id}
              data-testid={`open-house-${e.id}`}
              className="rounded-md border border-stone-200 bg-white/60 p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={e.date}
                  onChange={(ev) => patch(e.id, { date: ev.target.value })}
                  data-testid={`open-house-date-${e.id}`}
                  className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs tabular-nums font-medium"
                />
                <select
                  value={e.status}
                  onChange={(ev) => patch(e.id, { status: ev.target.value })}
                  data-testid={`open-house-status-${e.id}`}
                  className={`h-8 rounded-full border px-2.5 text-[11px] uppercase tracking-wider font-semibold ${OH_STATUS_STYLES[e.status] || OH_STATUS_STYLES.scheduled}`}
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                {needsSecurityNudge && (
                  <button
                    type="button"
                    onClick={openSecurityCheckModal}
                    data-testid={`open-house-security-nudge-${e.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-800 h-8 px-2.5 text-[11px] font-semibold uppercase tracking-wider transition-colors"
                    title="No security check logged yet today — click to log one"
                  >
                    <ShieldCheck className="h-3 w-3" />
                    Log security check
                  </button>
                )}
                <button
                  onClick={() => setToDelete(e)}
                  data-testid={`open-house-delete-${e.id}`}
                  className="ml-auto text-stone-400 hover:text-rose-600 transition-colors"
                  aria-label="Remove open house"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <Textarea
                value={e.notes || ""}
                onChange={(ev) => patch(e.id, { notes: ev.target.value })}
                data-testid={`open-house-notes-${e.id}`}
                placeholder="Seller notes / instructions (occupants, pets, staging, entry access…)"
                rows={2}
                className="mt-2 text-xs border-stone-300 bg-white/80"
              />
            </li>
            );
          })}
        </ul>
      )}

      {adding ? (
        <div className="rounded-md border border-[#010082]/20 bg-[#010082]/[0.04] p-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={draft.date}
              onChange={(ev) => setDraft((d) => ({ ...d, date: ev.target.value }))}
              data-testid="open-house-new-date"
              className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs tabular-nums font-medium"
              autoFocus
            />
            <select
              value={draft.status}
              onChange={(ev) => setDraft((d) => ({ ...d, status: ev.target.value }))}
              data-testid="open-house-new-status"
              className="h-8 rounded-full border border-stone-300 bg-white px-2.5 text-[11px] uppercase tracking-wider font-semibold text-stone-700"
            >
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <Textarea
            value={draft.notes}
            onChange={(ev) => setDraft((d) => ({ ...d, notes: ev.target.value }))}
            data-testid="open-house-new-notes"
            placeholder="Seller notes / instructions (occupants, pets, staging, entry access…)"
            rows={2}
            className="mt-2 text-xs border-stone-300 bg-white"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="ghost"
              onClick={() => { setAdding(false); setDraft({ date: "", status: "scheduled", notes: "" }); }}
              className="h-8 px-3 text-stone-500 text-xs"
            >
              Cancel
            </Button>
            <Button
              onClick={create}
              disabled={busy || !draft.date}
              data-testid="open-house-save"
              className="h-8 px-3 rounded-full text-white text-xs"
              style={{ backgroundColor: "#010082" }}
            >
              Save open house
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-testid="add-open-house-btn"
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-[#010082] hover:opacity-70 transition-opacity"
        >
          <CalendarPlus className="h-3.5 w-3.5" />
          Add open house event
        </button>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Remove open house?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the open house on {toDelete?.date}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(toDelete.id)} className="bg-rose-600 hover:bg-rose-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function TaskRow({ task, state, onChange, comments = [], listing = null, onListingUpdate = null }) {
  const [notes, setNotes] = useState(state?.notes || "");
  const [date, setDate] = useState(state?.date || "");
  const [link, setLink] = useState(state?.link || "");
  const [waivedReasonDraft, setWaivedReasonDraft] = useState(state?.waived_reason || "");
  const [waivedError, setWaivedError] = useState("");

  useEffect(() => {
    setNotes(state?.notes || "");
    setDate(state?.date || "");
    setLink(state?.link || "");
    setWaivedReasonDraft(state?.waived_reason || "");
    setWaivedError("");
  }, [state?.notes, state?.date, state?.link, state?.waived_reason]);

  const debouncedNotes = useDebouncedCallback((v) => onChange({ notes: v }), 500);
  const debouncedLink = useDebouncedCallback((v) => onChange({ link: v.trim() }), 500);
  const debouncedWaivedReason = useDebouncedCallback((v) => {
    const trimmed = v.trim();
    if (!trimmed) return;
    onChange({ waived_reason: trimmed });
  }, 600);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = date ? new Date(date) : null;
  if (dueDate) dueDate.setHours(0, 0, 0, 0);
  const overdue = dueDate && !state?.done && dueDate < today;
  const soon = dueDate && !state?.done && (dueDate - today) / 86400000 <= 7 && !overdue;

  const toggleSub = (subId, val) => {
    onChange({ subtasks: { [subId]: val } });
  };

  return (
    <div
      data-testid={`task-row-${task.id}`}
      className="border-t border-stone-100 py-4 first:border-t-0 first:pt-0"
    >
      <div className="flex items-start gap-3.5">
        <Checkbox
          data-testid={`task-checkbox-${task.id}`}
          checked={!!state?.done}
          onCheckedChange={(v) => onChange({ done: !!v })}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <p
              className={`text-sm leading-relaxed ${
                state?.done ? "line-through text-stone-400" : "text-stone-800"
              }`}
            >
              {task.label}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Input
                data-testid={`task-date-${task.id}`}
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  onChange({ date: e.target.value });
                }}
                className={`h-8 text-xs w-40 border-stone-300 ${
                  overdue ? "border-rose-300 bg-rose-50" : soon ? "border-amber-300 bg-amber-50" : ""
                }`}
              />
              {task.id !== "t5_2" && (
                <Input
                  data-testid={`task-notes-${task.id}`}
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    debouncedNotes(e.target.value);
                  }}
                  placeholder="Initials / notes"
                  className="h-8 text-xs w-40 border-stone-300"
                />
              )}
            </div>
          </div>

          {task.id === "t5_2" && (
            <div className="mt-3">
              <Textarea
                data-testid={`task-notes-${task.id}`}
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  debouncedNotes(e.target.value);
                }}
                placeholder="Log visitor and showing-agent follow-ups, feedback, and next steps…"
                rows={4}
                className="text-xs border-stone-300 bg-white leading-relaxed"
              />
            </div>
          )}

          {overdue && (
            <div className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-rose-600">
              <AlertTriangle className="h-3 w-3" />
              Overdue
            </div>
          )}

          {task.subtasks && task.subtasks.length > 0 && (
            <ul className={`mt-3.5 space-y-2 pl-1 ${state?.waived ? "opacity-40 pointer-events-none" : ""}`}>
              {task.subtasks.map((sub) => {
                const subDone = !!state?.subtasks?.[sub.id];
                const subStamp = state?.subtask_completed_at?.[sub.id];
                return (
                  <li key={sub.id} className="flex items-center gap-2.5">
                    <Checkbox
                      data-testid={`subtask-${sub.id}`}
                      checked={subDone}
                      onCheckedChange={(v) => toggleSub(sub.id, !!v)}
                      disabled={!!state?.waived}
                    />
                    <span className="text-xs text-stone-600">
                      {sub.label}
                      {sub.optional && (
                        <span className="ml-1.5 text-[10px] uppercase tracking-wider text-stone-400 font-medium">
                          Optional
                        </span>
                      )}
                      {subDone && subStamp && (
                        <span
                          className="ml-2 text-[10px] uppercase tracking-wider text-stone-400 font-medium tabular-nums"
                          data-testid={`subtask-stamp-${sub.id}`}
                        >
                          {new Date(subStamp).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {task.id === "t2_4" && (
            <div
              data-testid="waived-block-t2_4"
              className={`mt-4 rounded-md border p-3.5 ${
                state?.waived
                  ? "border-amber-300 bg-amber-50/60"
                  : "border-stone-200 bg-stone-50/60"
              }`}
            >
              <div className="flex items-start gap-2.5">
                <Checkbox
                  data-testid="task-waived-t2_4"
                  checked={!!state?.waived}
                  onCheckedChange={(v) => {
                    if (v) {
                      const reason = (waivedReasonDraft || "").trim();
                      if (!reason) {
                        setWaivedError("Please add a brief note explaining why pre-listing inspections were not ordered.");
                        return;
                      }
                      setWaivedError("");
                      onChange({ waived: true, waived_reason: reason });
                    } else {
                      setWaivedError("");
                      onChange({ waived: false, waived_reason: "" });
                    }
                  }}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-stone-800">
                      Pre-listing inspections not ordered / seller opted out
                    </span>
                    {state?.waived && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-200 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-800 font-semibold">
                        Waived
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-stone-500 mt-0.5 leading-relaxed">
                    Marks this task complete without pre-listing inspections. A brief note is required and this does not waive or release any future buyer inspection contingencies.
                  </p>
                  <Textarea
                    data-testid="task-waived-reason-t2_4"
                    value={waivedReasonDraft}
                    onChange={(e) => {
                      setWaivedReasonDraft(e.target.value);
                      if (waivedError) setWaivedError("");
                      if (state?.waived) debouncedWaivedReason(e.target.value);
                    }}
                    placeholder="e.g. Seller elected not to order pre-listing inspections; sold as-is; buyer to conduct own inspections…"
                    rows={2}
                    className="mt-2 text-xs border-stone-300 bg-white leading-relaxed"
                  />
                  {waivedError && (
                    <div className="mt-1.5 text-[11px] font-medium text-rose-600" data-testid="task-waived-error-t2_4">
                      {waivedError}
                    </div>
                  )}
                  {state?.waived && state?.waived_at && (
                    <div className="mt-2 text-[10px] uppercase tracking-wider text-stone-500 font-medium">
                      Marked waived on {new Date(state.waived_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      {state?.waived_by ? ` · by ${state.waived_by}` : ""}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {task.linkable && (
            <div className="mt-4">
              <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5 block">
                {task.linkLabel || "Link"}
              </Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="url"
                  value={link}
                  data-testid={`task-link-${task.id}`}
                  onChange={(e) => {
                    setLink(e.target.value);
                    debouncedLink(e.target.value);
                  }}
                  placeholder="https://drive.google.com/… or dotloop link"
                  className="h-9 text-sm bg-white border-stone-300 flex-1 min-w-[240px]"
                />
                {link && (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`task-link-open-${task.id}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#010082]/30 text-[#010082] hover:bg-[#010082]/[0.06] text-xs font-semibold uppercase tracking-widest"
                  >
                    Open
                    <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9L9 3M9 3H4.5M9 3v4.5"/></svg>
                  </a>
                )}
              </div>
              <p className="text-[11px] text-stone-500 mt-1.5 leading-relaxed">
                Shared with the seller in tonight's digest email and on their share portal.
              </p>
            </div>
          )}


          {comments.length > 0 && (
            <div
              data-testid={`task-comments-${task.id}`}
              className="mt-4 rounded-md border border-[#010082]/15 bg-[#010082]/[0.04] p-3.5"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#010082] font-semibold mb-2.5">
                <MessageSquare className="h-3 w-3" />
                Seller notes · {comments.length}
              </div>
              <ul className="space-y-3">
                {comments.map((c) => (
                  <li key={c.id} data-testid={`task-comment-${c.id}`}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-semibold text-stone-800">{c.author_name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-stone-400">
                        {new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        {" · "}
                        {new Date(c.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                      </span>
                    </div>
                    <p className="text-xs text-stone-700 leading-relaxed mt-1 whitespace-pre-wrap italic">
                      "{c.text}"
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {task.id === "t5_1" && listing && onListingUpdate && (
            <OpenHouseEvents listing={listing} onListingUpdate={onListingUpdate} />
          )}
        </div>
      </div>
    </div>
  );
}

export default function ChecklistView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [dealFellThroughOpen, setDealFellThroughOpen] = useState(false);
  const [dealFellReason, setDealFellReason] = useState("");
  const [dealFellSubmitting, setDealFellSubmitting] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("deadlines");
  useDocumentTitle(listing?.property_address);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/listings/${id}`);
      setListing(data);
      // Auto-mark seller comments as read when the agent opens this listing
      const comments = data.task_comments || [];
      const lastRead = data.agent_last_read_comments_at;
      const hasUnread = comments.some(
        (c) => c.author === "seller" && (!lastRead || c.created_at > lastRead)
      );
      if (hasUnread) {
        try {
          const { data: updated } = await api.post(`/listings/${id}/comments/mark-read`);
          setListing(updated);
        } catch (_) {
          /* best-effort */
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
      nav("/");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => {
    load();
  }, [load]);

  const updateTask = async (taskId, patch) => {
    setListing((prev) => {
      if (!prev) return prev;
      const cur = prev.tasks?.[taskId] || { done: false, date: null, notes: "", subtasks: {} };
      const next = { ...cur, ...patch };
      if (patch.subtasks) next.subtasks = { ...(cur.subtasks || {}), ...patch.subtasks };
      return { ...prev, tasks: { ...(prev.tasks || {}), [taskId]: next } };
    });
    try {
      const { data } = await api.patch(`/listings/${id}/task`, { task_id: taskId, ...patch });
      setListing(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
      load();
    }
  };

  const overall = useMemo(() => (listing ? computeProgress(listing) : { done: 0, total: 0, pct: 0 }), [listing]);

  if (loading || !listing) {
    return (
      <div className="min-h-screen bg-[#faf7f2]">
        <Header />
        <div className="mx-auto max-w-7xl px-6 py-16 flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-stone-300 border-t-stone-900" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <Header />
      <main className="mx-auto max-w-7xl px-6 py-12 lg:py-16">
        <Link
          to="/app"
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900 mb-8"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          All homes
        </Link>

        {/* Editorial hero */}
        <div className="mb-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div className="min-w-0 max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <p className="overline text-stone-500">Listing Workflow</p>
              <span className="text-stone-300">·</span>
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${
                  STATUS_STYLES[listing.listing_status] || STATUS_STYLES["Pre-Listing"]
                }`}
              >
                {listing.listing_status}
              </span>
            </div>
            <h1
              data-testid="listing-title"
              className="text-4xl sm:text-5xl font-heading font-medium tracking-tight text-stone-900 leading-[1.05]"
            >
              {listing.property_address || "Untitled property"}
            </h1>
            <p className="text-sm text-stone-500 mt-4 max-w-xl">
              A guided seven-phase workflow so nothing slips — from seller intake through post-closing follow-up.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {listing.listing_status === "Closed" && listing.seller_email && (
              <Button
                data-testid="send-closing-email-button"
                variant="outline"
                onClick={async () => {
                  try {
                    await api.post(`/reminders/send-closing-email/${listing.id}`);
                    setListing((prev) => ({ ...prev, closing_email_sent: true }));
                    toast.success("Closing email sent to seller");
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
                  }
                }}
                className="border-stone-300 rounded-full px-4"
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                {listing.closing_email_sent ? "Resend closing email" : "Send closing email"}
              </Button>
            )}
            {listing.seller_email && (
              <Button
                data-testid="send-seller-update-button"
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/reminders/send-seller-update/${listing.id}`);
                    const parts = [];
                    if (data.completed_count) parts.push(`${data.completed_count} task${data.completed_count === 1 ? "" : "s"}`);
                    if (data.notes_count) parts.push(`${data.notes_count} note${data.notes_count === 1 ? "" : "s"}`);
                    toast.success(parts.length ? `Sent · ${parts.join(" + ")}` : "Sent");
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
                  }
                }}
                className="border-stone-300 rounded-full px-4"
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                Send seller today's update
              </Button>
            )}
            {listing.seller_email && (
              <Button
                data-testid="send-seller-weekly-button"
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/reminders/send-seller-weekly/${listing.id}`);
                    const parts = [];
                    if (data.completed_count) parts.push(`${data.completed_count} task${data.completed_count === 1 ? "" : "s"}`);
                    if (data.open_houses_count) parts.push(`${data.open_houses_count} open house${data.open_houses_count === 1 ? "" : "s"}`);
                    if (data.security_checks_count) parts.push(`${data.security_checks_count} security check${data.security_checks_count === 1 ? "" : "s"}`);
                    if (data.notes_count) parts.push(`${data.notes_count} note${data.notes_count === 1 ? "" : "s"}`);
                    toast.success(parts.length ? `Weekly recap sent · ${parts.join(" + ")}` : "Weekly recap sent");
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
                  }
                }}
                className="border-stone-300 rounded-full px-4"
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                Send weekly recap
              </Button>
            )}
            {listing.target_list_date && (
              <Button
                data-testid="apply-suggested-deadlines-button"
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/listings/${listing.id}/apply-suggested-deadlines`);
                    setListing(data);
                    toast.success("Suggested deadlines applied");
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not apply");
                  }
                }}
                className="border-stone-300 rounded-full px-4"
              >
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                Suggest deadlines
              </Button>
            )}
            <Button
              data-testid="edit-listing-button"
              variant="outline"
              onClick={() => setEditing(true)}
              className="border-stone-300 rounded-full px-4"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit details
            </Button>
            {listing.listing_status !== "Cancelled" && listing.listing_status !== "Withdrawn" && (
              <Button
                data-testid="deal-fell-through-button"
                variant="outline"
                onClick={() => {
                  setDealFellReason("");
                  setDealFellThroughOpen(true);
                }}
                className="border-amber-300 text-amber-800 hover:bg-amber-50 rounded-full px-4"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                Deal fell through
              </Button>
            )}
            {listing.listing_status !== "Cancelled" && (
              <Button
                data-testid="cancel-listing-button"
                variant="outline"
                onClick={() => {
                  setCancelReason("");
                  setCancelOpen(true);
                }}
                className="border-rose-300 text-rose-700 hover:bg-rose-50 rounded-full px-4"
              >
                <Ban className="h-3.5 w-3.5 mr-1.5" />
                Cancel listing
              </Button>
            )}
          </div>
        </div>

        <div className="hr-serif mb-10" />

        <ListingTabs active={activeTab} onChange={setActiveTab} listing={listing} />

        {activeTab === "intake" && (
          <div className="pb-10" data-testid="tab-intake-panel">
            <IntakeDetails listing={listing} onEdit={() => nav(`/listing/${listing.id}/intake`)} />
          </div>
        )}

        {activeTab === "transaction" && !isTransactionHubUnlocked(listing) && (
          <div className="rounded-md border border-dashed border-stone-300 bg-white p-12 text-center" data-testid="transaction-hub-locked">
            <Lock className="h-6 w-6 text-stone-400 mx-auto" />
            <p className="overline text-stone-500 mt-3">Transaction Hub</p>
            <h3 className="font-heading text-2xl font-semibold text-stone-900 mt-1">
              Unlocks once an <em className="italic font-normal">offer is accepted</em>.
            </h3>
            <p className="text-sm text-stone-500 mt-2 max-w-md mx-auto leading-relaxed">
              Mark Phase 06 · "Accept offer" complete to open the contract-to-close workspace with escrow milestones, Form VP and the ratified agreement.
            </p>
          </div>
        )}

        {(activeTab === "deadlines" || (activeTab === "transaction" && isTransactionHubUnlocked(listing))) && (
        <>
        {activeTab === "transaction" && (
          <>
            <div className="mb-4 flex items-center justify-end">
              <SendTCPackageButton listing={listing} onListingUpdate={setListing} />
            </div>
            <CollaboratorsCard listing={listing} onListingUpdate={setListing} />
            <CollaboratorCommentsFeed listing={listing} onListingUpdate={setListing} />
          </>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid={`tab-${activeTab}-panel`}>
          <section className="lg:col-span-8">
            <Accordion type="multiple" defaultValue={PHASES.map((p) => p.id)} className="space-y-4">
              {PHASES.filter((p) => activeTab === "transaction" ? p.id === "p7" : true).map((phase) => {
                const pp = computePhaseProgress(listing, phase.id);
                const idx = String(phase.number).padStart(2, "0");
                const isComplete = pp.total > 0 && pp.done >= pp.total;
                return (
                  <AccordionItem
                    key={phase.id}
                    value={phase.id}
                    data-testid={`phase-${phase.id}`}
                    data-complete={isComplete ? "true" : "false"}
                    className={`rounded-md border px-7 py-2 [&[data-state=open]]:shadow-sm transition-colors ${
                      isComplete
                        ? "border-[#010082]/25 bg-[#010082]/[0.045]"
                        : "border-stone-200 bg-white"
                    }`}
                  >
                    <AccordionTrigger className="hover:no-underline py-5">
                      <div className="flex flex-1 items-start gap-6 pr-3 text-left">
                        <div className="shrink-0 pt-1">
                          <span className="section-index text-stone-500 text-sm block">Phase {idx}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xl font-heading font-semibold tracking-tight text-stone-900 leading-tight">
                            {phase.title}
                          </p>
                          <p className="text-sm text-stone-500 mt-1 leading-relaxed">
                            {phase.description}
                          </p>
                          {phase.id === "p7" && listing.closing_date && (
                            <ClosingCountdownPill closingDate={listing.closing_date} />
                          )}
                        </div>
                        <div className="hidden sm:flex items-center gap-3 w-40 shrink-0 pt-2">
                          <Progress value={pp.pct} className="h-1 flex-1 bg-stone-100" />
                          <span className="text-xs tabular-nums text-stone-500 w-10 text-right font-heading">
                            {pp.done}/{pp.total}
                          </span>
                        </div>
                        {isComplete && (
                          <span
                            data-testid={`phase-complete-chip-${phase.id}`}
                            className="hidden sm:inline-flex items-center gap-1 rounded-full h-6 px-2.5 text-[10px] font-semibold uppercase tracking-widest text-white shrink-0 self-start mt-2"
                            style={{ backgroundColor: "#010082" }}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Complete
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pt-2">
                      <div className="border-t border-stone-100 pt-5">
                        {phase.id === "p7" && (
                          <RPAUploadCard listing={listing} onListingUpdate={setListing} />
                        )}
                        {phase.tasks.map((task) => (
                          <div key={task.id}>
                            <TaskRow
                              task={task}
                              state={listing.tasks?.[task.id]}
                              comments={(listing.task_comments || []).filter((c) => c.task_id === task.id)}
                              listing={listing}
                              onListingUpdate={setListing}
                              onChange={(patch) => updateTask(task.id, patch)}
                            />
                            {task.id === "t6_commission_demand" && (
                              <CommissionDemandInline listing={listing} onListingUpdate={setListing} />
                            )}
                            {task.id === "t6_5" && (
                              <>
                                <SellerSigningInline listing={listing} onListingUpdate={setListing} />
                                <CloseOfEscrowInline listing={listing} onListingUpdate={setListing} />
                                <SellerSigningChecklistLink />
                              </>
                            )}
                            {task.id === "t6_7" && (
                              <SoldMlsUploadCard listing={listing} onListingUpdate={setListing} />
                            )}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </section>

          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-md border border-stone-200 bg-white p-6">
              <p className="overline text-stone-500 mb-3">Overall Progress</p>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-5xl font-heading font-medium tracking-tight text-stone-900 tabular-nums">
                  {overall.pct}
                </span>
                <span className="text-lg font-heading text-stone-400">%</span>
                <span className="text-xs text-stone-500 tabular-nums ml-auto">
                  {overall.done} of {overall.total} tasks
                </span>
              </div>
              <Progress value={overall.pct} className="h-1.5 bg-stone-100" />
            </div>

            <div className="rounded-md border border-stone-200 bg-white p-6">
              <p className="overline text-stone-500 mb-4">Home Info</p>
              <dl className="space-y-3.5 text-sm">
                <div className="flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
                  <span className="text-stone-800">{listing.property_address || "—"}</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <User className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-stone-800">{[listing.seller_names, listing.seller_2_name].filter(Boolean).join(" & ") || "—"}</div>
                    {listing.listing_agent && (
                      <div className="text-xs text-stone-500 mt-0.5">Agent · {listing.listing_agent}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Calendar className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
                  <span className="text-stone-800">
                    {listing.target_list_date
                      ? new Date(listing.target_list_date).toLocaleDateString(undefined, {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "No target date"}
                  </span>
                </div>
                {listing.closing_date && (
                  <div className="flex items-start gap-2.5" data-testid="agent-closing-date-row">
                    <Calendar className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Target close</div>
                      <div className="text-stone-800">
                        {new Date(listing.closing_date).toLocaleDateString(undefined, {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                    </div>
                  </div>
                )}
                {listing.mls_number && (
                  <div className="flex items-start gap-2.5">
                    <Hash className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
                    <span className="text-stone-800 font-mono text-xs">{listing.mls_number}</span>
                  </div>
                )}
              </dl>
              {listing.general_notes && (
                <div className="mt-5 border-t border-stone-100 pt-4">
                  <p className="overline text-stone-500 mb-2">Notes</p>
                  <p className="text-xs text-stone-700 whitespace-pre-wrap leading-relaxed">{listing.general_notes}</p>
                </div>
              )}
              {(listing.escrow_company || listing.escrow_officer_name || listing.escrow_officer_email || listing.escrow_officer_phone || listing.escrow_number) && (
                <div className="mt-5 border-t border-stone-100 pt-4" data-testid="agent-escrow-block">
                  <p className="overline text-stone-500 mb-2">Escrow contact</p>
                  <dl className="space-y-1.5 text-xs text-stone-700 leading-relaxed">
                    {listing.escrow_company && (
                      <div className="font-semibold text-stone-900">{listing.escrow_company}</div>
                    )}
                    {listing.escrow_officer_name && (
                      <div>{listing.escrow_officer_name}</div>
                    )}
                    {listing.escrow_number && (
                      <div className="text-stone-500">
                        <span className="text-[10px] uppercase tracking-widest mr-1.5">Escrow #</span>
                        <span className="font-mono text-stone-800">{listing.escrow_number}</span>
                      </div>
                    )}
                    {listing.escrow_officer_email && (
                      <a href={`mailto:${listing.escrow_officer_email}`} className="block text-stone-600 hover:text-brand break-all">
                        {listing.escrow_officer_email}
                      </a>
                    )}
                    {listing.escrow_officer_phone && (
                      <a href={`tel:${listing.escrow_officer_phone.replace(/[^0-9+]/g, "")}`} className="block text-stone-600 hover:text-brand">
                        {listing.escrow_officer_phone}
                      </a>
                    )}
                  </dl>
                </div>
              )}
            </div>

            <UpcomingWidget listings={[listing]} />

            <ShareCard listing={listing} onUpdate={setListing} />

            {listing.seller_email && <EmailEngagementCard listingId={listing.id} />}
          </aside>
        </div>
        </>
        )}

        <ActivityLog listing={listing} onListingUpdate={setListing} />
      </main>

      <CreateListingDialog
        open={editing}
        onOpenChange={setEditing}
        listingId={listing.id}
        initial={listing}
        onCreated={(updated) => {
          setListing((prev) => ({ ...prev, ...updated }));
          setEditing(false);
          toast.success("Home updated");
        }}
      />

      <AlertDialog open={dealFellThroughOpen} onOpenChange={setDealFellThroughOpen}>
        <AlertDialogContent data-testid="deal-fell-through-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Deal fell through?</AlertDialogTitle>
            <AlertDialogDescription>
            This resets <b>Phase 6 (Offers)</b> and <b>Phase 7 (Escrow & Closing)</b> tasks — completions, dates, notes, and links — and clears all escrow contact / signing / closing details so a fresh buyer can be tracked. Pre-listing work (disclosures, media, MLS, open houses) is preserved. A note with your reason is added to the activity log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-1">
            <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5 block">
              Reason (optional but recommended)
            </Label>
            <Textarea
              data-testid="deal-fell-through-reason"
              value={dealFellReason}
              onChange={(e) => setDealFellReason(e.target.value)}
              placeholder="e.g. Buyer's financing fell through; buyer walked after inspection…"
              rows={3}
              className="text-sm border-stone-300 bg-white leading-relaxed"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deal-fell-through-cancel">Nevermind</AlertDialogCancel>
            <AlertDialogAction
              data-testid="deal-fell-through-confirm"
              disabled={dealFellSubmitting}
              onClick={async (e) => {
                e.preventDefault();
                setDealFellSubmitting(true);
                try {
                  const { data } = await api.post(`/listings/${id}/deal-fell-through`, { reason: dealFellReason });
                  setListing(data);
                  toast.success("Deal reset — ready for the next buyer");
                  setDealFellThroughOpen(false);
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset");
                } finally {
                  setDealFellSubmitting(false);
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {dealFellSubmitting ? "Resetting…" : "Reset buyer & escrow"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="cancel-listing-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Cancel this listing?</AlertDialogTitle>
            <AlertDialogDescription>
              Sets status to <b>Cancelled</b> and turns off all seller emails and reminders. The listing and all history remain intact — you can re-activate later from Home Details. A note with your reason is added to the activity log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-1">
            <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5 block">
              Reason (optional)
            </Label>
            <Textarea
              data-testid="cancel-listing-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Seller decided to hold off; listing agreement expired…"
              rows={3}
              className="text-sm border-stone-300 bg-white leading-relaxed"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-listing-cancel">Nevermind</AlertDialogCancel>
            <AlertDialogAction
              data-testid="cancel-listing-confirm"
              disabled={cancelSubmitting}
              onClick={async (e) => {
                e.preventDefault();
                setCancelSubmitting(true);
                try {
                  const { data } = await api.post(`/listings/${id}/cancel`, { reason: cancelReason });
                  setListing(data);
                  toast.success("Listing cancelled");
                  setCancelOpen(false);
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not cancel");
                } finally {
                  setCancelSubmitting(false);
                }
              }}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {cancelSubmitting ? "Cancelling…" : "Cancel listing"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
